import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation';
import { overviewColors, DataPoint } from './ProductChartUtils';
import { drawLinePlugin } from '@UI-Elements/ChartPlugins/DrawLinePlugin';
import { handleMouseDown, handleMouseMove, handleMouseUp, ChartDragging } from '@UI-Elements/ChartPlugins/ChartUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, annotationPlugin);

interface IChartProps {
  dataPoints: DataPoint[];
  onDataClick: (_forDate: string, _productPrice: number, _materialPrice: number, _futurePrice: number) => void;
}
export const ProductOverviewChart: React.FC<IChartProps> = ({ onDataClick, dataPoints }) => {
  const chartRef = React.useRef<ChartJS<'line'> | null>(null);

  const chartData = {
    labels: dataPoints.map(({ date }) => date),
    datasets: [
      {
        label: 'Product price per unit',
        data: dataPoints.map(({ product }) => product),
        borderColor: overviewColors.product,
        backgroundColor: overviewColors.product,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Raw Material price per ton',
        data: dataPoints.map(({ material }) => material),
        borderColor: overviewColors.material,
        backgroundColor: overviewColors.material,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Financial raw material cost',
        data: dataPoints.map(({ future }) => future),
        borderColor: overviewColors.future,
        backgroundColor: overviewColors.future,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: false,

    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
      title: {
        display: false,
        text: 'History Chart',
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
      },
    },
    animation: {
      onComplete: (animEvent) => {
        const chart = animEvent.chart as ChartDragging;
        if (!chart.dragPoint?.x) {
          chart.dragPoint = { x: 500, y: 500 };
          const dataIndex = 10;
          const label = chartData.labels[dataIndex];
          const productPrice = chartData.datasets[0].data[dataIndex];

          const materialPrice = chartData.datasets[1].data[dataIndex];
          const financialCost = chartData.datasets[2].data[dataIndex];
          const { top, bottom } = chart.scales.y;

          chart.clicked = { topY: top, bottomY: bottom, x: 500 };
          onDataClick(label, productPrice, materialPrice, financialCost);
        }
      },
    },
    onHover: (event: any) => {
      if (event.type === 'click' || (event.chart.isDragging && event.type === 'mousemove')) {
        const activeElements = event.chart.tooltip?.getActiveElements();
        if (activeElements?.length) {
          const {
            index: dataIndex,
            element: { x },
          } = activeElements[0];
          const topY = event.chart.scales.y.top;
          const bottomY = event.chart.scales.y.bottom;

          const label = chartData.labels[dataIndex];
          const productPrice = chartData.datasets[0].data[dataIndex];

          const materialPrice = chartData.datasets[1].data[dataIndex];
          const financialCost = chartData.datasets[2].data[dataIndex];

          const clickedData = { dataIndex, topY, bottomY, x };

          event.chart.clicked = clickedData;
          onDataClick(label, productPrice, materialPrice, financialCost);
        }
      }
    },
  };

  React.useEffect(() => {
    const chart = chartRef.current;
    if (chart?.canvas) {
      chart.canvas.addEventListener('mousedown', handleMouseDown(chart));
      chart.canvas.addEventListener('mousemove', handleMouseMove(chart));
      chart.canvas.addEventListener('mouseup', handleMouseUp(chart));
    }

    return () => {
      if (chart?.canvas) {
        chart.canvas.removeEventListener('mousemove', handleMouseMove(chart));
        chart.canvas.removeEventListener('mousedown', handleMouseDown(chart));
        chart.canvas.removeEventListener('mouseup', handleMouseUp(chart));
      }
    };
  }, []);

  return (
    <Line
      width={1100} // static size - move to argument if needed
      height={380}
      className={`flex h-full w-full `}
      data={chartData}
      options={options}
      plugins={[drawLinePlugin]}
      ref={chartRef}
    />
  );
};
